/**
 * Her, as a shape, in the diary rather than on the stage.
 *
 * The stage has had a mark since the first meeting: something lit, breathing,
 * with the room pushing out from it in rings. This is the same drawing brought
 * into the ordinary app, where it does a job the stage version never had to —
 * it has to say *who is talking*. So it appears exactly where she does: beside
 * the greeting she writes each morning, inside a toast in her voice, in front
 * of a wait she is covering for. Never as decoration on a screen she has
 * nothing to do with.
 *
 * The eyes are subtracted from the light rather than drawn on it, via a mask.
 * See `mark.css` for why that matters more than it sounds like it should.
 * Below `EYES_MIN_PX` they are dropped entirely: two holes a pixel and a half
 * wide are not subtle, they are dirt on the screen.
 */

import { cn } from '@gramas/ui'
import { type CSSProperties, useEffect, useId, useRef, useState } from 'react'
import { type MarkSignal, useMarkSignal, watchTaps } from './markSignal.js'
import { type Station, usePresenceClaim } from './presenceRegistry.js'

/** Below this the eyes stop reading as eyes and start reading as artefacts. */
const EYES_MIN_PX = 18

/** Gaps between blinks. Uneven on purpose: a metronome blink is a cursor. */
const BLINK_MIN_MS = 2600
const BLINK_MAX_MS = 7200
/** Cats double-blink. Rarely enough that it reads as a tic rather than a loop. */
const DOUBLE_BLINK_CHANCE = 0.18
/** A glance that rides along with a blink, which is how eyes usually move. */
const GAZE_ON_BLINK_CHANCE = 0.45
/** Looking about with the eyes open, on its own slower clock. */
const WANDER_MIN_MS = 7000
const WANDER_MAX_MS = 19_000
/** The rare whole-head turn. */
const IDLE_MIN_MS = 24_000
const IDLE_MAX_MS = 70_000
/** How long she keeps her eyes on the last thing you touched. */
const TAP_LOOK_MS = 1600
/**
 * How far the eyes travel, in viewBox units, at the extremes. The eyes sit
 * 4.5 units clear of the rim on either side, so this leaves the light around
 * them thin at full stretch but never broken — which is the look of an eye
 * gone right to the corner of its socket.
 */
const GAZE_X_RANGE = 2.6
const GAZE_Y_RANGE = 1.5

interface Gaze {
  x: number
  y: number
}

const CENTRE: Gaze = { x: 0, y: 0 }

/** Somewhere to look that is not straight ahead, and not the same place twice. */
function someDirection(): Gaze {
  const x = (Math.random() < 0.5 ? -1 : 1) * (0.55 + Math.random() * 0.45)
  const y = (Math.random() - 0.5) * 1.2
  return { x, y }
}

/** Squash to [-1, 1] with a soft knee: a tap far away is not further than far. */
function toward(delta: number, reach: number): number {
  return Math.max(-1, Math.min(1, delta / reach))
}

export interface GlitimeMarkProps {
  /** Rendered size in px. The glow and eye scale follow it. */
  size?: number
  /**
   * React to `pulseMark()` / `flickerMark()`. On for the marks that represent
   * her presence; off for the one inside a toast, which is already the event.
   */
  live?: boolean
  /** Blink and glance. Off where the mark is decorative or very small. */
  alive?: boolean
  /**
   * Petting: a held finger (≥600 ms) on the mark itself. The mark plays its
   * slow-blink either way; the callback decides whether a line goes with it.
   * Wired wherever the mark stands in for her presence, including a persona
   * toast's mark — the utterance is the message text, but the mark riding
   * along with it is still her, so it takes the same gesture (see
   * `ToastCard.tsx`, which stops the gesture at the mark so it never also
   * triggers the card's own hold-to-read). The hold swallows the click that
   * would follow, so a mark inside a button can be petted without triggering
   * the button.
   */
  onPet?: () => void
  /**
   * A held pose for the conversation surface: `thinking` drifts the eyes
   * up-left and slows the rings while the model works; `listening` turns them
   * toward the mic. Overrides the idle wander while set.
   */
  pose?: 'thinking' | 'listening' | null
  /**
   * Which room this instance stands in, for the presence registry — omitted
   * for marks that never compete for the awake slot (the login screen, the
   * dev lab), which render as if always awake.
   */
  station?: Station
  className?: string
}

function between(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** A finger resting this long on her is a pet, not a tap. */
const PET_HOLD_MS = 600
/** How long the slow-blink acknowledgement plays. */
const PET_PLAY_MS = 1100

/** Where the eyes settle for each held pose, in gaze units. */
const POSE_GAZE: Record<'thinking' | 'listening', Gaze> = {
  thinking: { x: -0.75, y: -0.85 },
  listening: { x: 0.85, y: 0.5 },
}

export function GlitimeMark({
  size = 28,
  live = false,
  alive = true,
  onPet,
  pose = null,
  station,
  className,
}: GlitimeMarkProps) {
  const maskId = useId()
  const showEyes = size >= EYES_MIN_PX
  const awake = usePresenceClaim(station)
  const [blinking, setBlinking] = useState(false)
  const [idling, setIdling] = useState(false)
  const [petting, setPetting] = useState(false)
  const [gaze, setGaze] = useState<Gaze>(CENTRE)
  const [signal, setSignal] = useState<MarkSignal | null>(null)
  const host = useRef<HTMLSpanElement>(null)
  const petHold = useRef<ReturnType<typeof setTimeout> | null>(null)
  const petPlay = useRef<ReturnType<typeof setTimeout> | null>(null)
  const seq = useMarkSignal((state) => state.seq)
  const kind = useMarkSignal((state) => state.kind)
  const tapSeq = useMarkSignal((state) => state.tapSeq)
  // The first render is not an event: without this the mark flares on mount
  // for whatever the last thing she said before this screen existed was.
  const seenSeq = useRef(seq)
  const seenTap = useRef(tapSeq)

  useEffect(() => {
    if (!live || seq === seenSeq.current) return
    seenSeq.current = seq
    setSignal(kind)
    const handle = setTimeout(() => setSignal(null), 700)
    return () => clearTimeout(handle)
  }, [live, seq, kind])

  // She looks at what you just touched.
  //
  // The single most alive thing a pair of eyes can do is turn out to have been
  // watching. Everything else here is on a timer and therefore, eventually,
  // legible as a loop; this one is the only motion in the app that answers you.
  // The blink a beat later is the tell that she registered it rather than
  // merely pointing at it.
  useEffect(() => {
    if (!alive || !awake || !showEyes || prefersReducedMotion()) {
      setGaze(CENTRE)
      return
    }
    if (tapSeq === seenTap.current) return
    seenTap.current = tapSeq
    const box = host.current?.getBoundingClientRect()
    if (box == null || box.width === 0) return

    const { tapX, tapY } = useMarkSignal.getState()
    // The reach is generous on purpose: a tap on the far side of the screen
    // should read as "over there", not as the same hard stare as a near one.
    setGaze({
      x: toward(tapX - (box.left + box.width / 2), Math.max(120, window.innerWidth * 0.34)),
      y: toward(tapY - (box.top + box.height / 2), Math.max(120, window.innerHeight * 0.3)),
    })
    const blinkHandle = setTimeout(() => setBlinking(true), 260)
    const openHandle = setTimeout(() => setBlinking(false), 420)
    const restHandle = setTimeout(() => setGaze(CENTRE), TAP_LOOK_MS)
    return () => {
      clearTimeout(blinkHandle)
      clearTimeout(openHandle)
      clearTimeout(restHandle)
    }
  }, [alive, awake, showEyes, tapSeq])

  useEffect(() => {
    if (!alive || !awake || !showEyes || prefersReducedMotion()) {
      setBlinking(false)
      return
    }
    watchTaps()
    let blinkHandle: ReturnType<typeof setTimeout>
    let closeHandle: ReturnType<typeof setTimeout>
    let againHandle: ReturnType<typeof setTimeout>

    const close = (then: () => void) => {
      setBlinking(true)
      closeHandle = setTimeout(() => {
        setBlinking(false)
        then()
      }, 190)
    }

    const schedule = () => {
      blinkHandle = setTimeout(
        () => {
          // A glance mostly rides along with a blink, which is how eyes
          // actually move — the standalone wander below is the exception.
          if (Math.random() < GAZE_ON_BLINK_CHANCE) {
            setGaze(someDirection())
            setTimeout(() => setGaze(CENTRE), between(1200, 2800))
          }
          close(() => {
            if (Math.random() < DOUBLE_BLINK_CHANCE) {
              againHandle = setTimeout(() => close(schedule), 150)
              return
            }
            schedule()
          })
        },
        between(BLINK_MIN_MS, BLINK_MAX_MS),
      )
    }
    schedule()

    return () => {
      clearTimeout(blinkHandle)
      clearTimeout(closeHandle)
      clearTimeout(againHandle)
    }
  }, [alive, awake, showEyes])

  // Looking about with her eyes open. Slower than blinking and deliberately
  // unhurried: the point is that she is somewhere else for a moment, not that
  // something is loading.
  useEffect(() => {
    if (!alive || !awake || !showEyes || prefersReducedMotion()) {
      setGaze(CENTRE)
      return
    }
    let outHandle: ReturnType<typeof setTimeout>
    let backHandle: ReturnType<typeof setTimeout>

    const schedule = () => {
      outHandle = setTimeout(
        () => {
          setGaze(someDirection())
          backHandle = setTimeout(
            () => {
              setGaze(CENTRE)
              schedule()
            },
            between(1100, 2600),
          )
        },
        between(WANDER_MIN_MS, WANDER_MAX_MS),
      )
    }
    schedule()

    return () => {
      clearTimeout(outHandle)
      clearTimeout(backHandle)
    }
  }, [alive, awake, showEyes])

  useEffect(() => {
    if (!alive || !awake || prefersReducedMotion()) {
      setIdling(false)
      return
    }
    let turnHandle: ReturnType<typeof setTimeout>
    let restHandle: ReturnType<typeof setTimeout>

    const schedule = () => {
      turnHandle = setTimeout(
        () => {
          setIdling(true)
          restHandle = setTimeout(() => {
            setIdling(false)
            schedule()
          }, 2700)
        },
        between(IDLE_MIN_MS, IDLE_MAX_MS),
      )
    }
    schedule()

    return () => {
      clearTimeout(turnHandle)
      clearTimeout(restHandle)
    }
  }, [alive, awake])

  // The false→true edge of `awake`: presence landing here, either on mount
  // (a toast arriving already claims the top rank) or by falling back once
  // whatever outranked her lets go. Reuses the same one-shot `signal` state
  // as `pulseMark()` rather than a second mechanism, so CSS only has to know
  // about one "flare".
  const wasAwake = useRef(awake)
  useEffect(() => {
    const waking = awake && !wasAwake.current
    wasAwake.current = awake
    if (!waking || prefersReducedMotion()) return
    setSignal('flare')
    const handle = setTimeout(() => setSignal(null), 700)
    return () => clearTimeout(handle)
  }, [awake])

  // Petting. A hold, not a tap: the tap already belongs to whatever button
  // the mark is sitting inside, and the whole point of a hold is that it is a
  // deliberate act rather than a navigation. When the hold fires, the click
  // that the browser will synthesise on release is swallowed once at the
  // document, so petting her never also opens her panel.
  useEffect(() => {
    return () => {
      if (petHold.current != null) clearTimeout(petHold.current)
      if (petPlay.current != null) clearTimeout(petPlay.current)
    }
  }, [])

  const petStart = () => {
    if (onPet == null) return
    if (petHold.current != null) clearTimeout(petHold.current)
    petHold.current = setTimeout(() => {
      petHold.current = null
      const swallow = (event: MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
      }
      document.addEventListener('click', swallow, { capture: true, once: true })
      setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 400)
      setPetting(true)
      if (petPlay.current != null) clearTimeout(petPlay.current)
      petPlay.current = setTimeout(() => setPetting(false), PET_PLAY_MS)
      onPet()
    }, PET_HOLD_MS)
  }

  const petCancel = () => {
    if (petHold.current == null) return
    clearTimeout(petHold.current)
    petHold.current = null
  }

  // A held pose wins over wherever the eyes were wandering.
  const shownGaze = pose != null ? POSE_GAZE[pose] : gaze

  // Drawn in a 32-unit box: core at r=10.5 leaves the rings somewhere to go
  // and the drop shadow somewhere to land without clipping.
  //
  // Wider than tall, at Katė's own 7:4 — the eyes on the meeting's last slide
  // are horizontal lozenges, and that is the drawing the household already
  // reads as "being watched". Upright ones, which is where this started, close
  // up into a snout the moment the mark is small enough to sit in a toast.
  const eyeGap = 3.4
  const eyeW = 5
  const eyeH = 2.9

  return (
    <span
      ref={host}
      aria-hidden="true"
      className={cn('glit-mark', className)}
      data-blink={blinking ? '1' : undefined}
      data-idle={idling ? '1' : undefined}
      data-signal={signal ?? undefined}
      data-pet={petting ? '1' : undefined}
      data-pose={pose ?? undefined}
      data-away={awake ? undefined : '1'}
      onPointerDown={onPet != null ? petStart : undefined}
      onPointerUp={onPet != null ? petCancel : undefined}
      onPointerLeave={onPet != null ? petCancel : undefined}
      onPointerCancel={onPet != null ? petCancel : undefined}
      style={
        {
          '--glit-size': `${size}px`,
          '--gaze': `${shownGaze.x * (size / 32) * GAZE_X_RANGE}px`,
          '--gaze-y': `${shownGaze.y * (size / 32) * GAZE_Y_RANGE}px`,
          width: size,
          height: size,
        } as CSSProperties
      }
    >
      <span className="glit-ring" />
      <span className="glit-ring" style={{ '--ring-d': '1400ms' } as CSSProperties} />
      {/* Width and height as attributes, not only as CSS: an SVG with neither
          falls back to 300×150, so if this file's stylesheet ever fails to
          load the mark is wrong rather than absent — and wrong is debuggable.
          `--her` carries its literal fallback for the same reason. She is not
          allowed to depend on a token resolving in order to exist. */}
      <svg className="glit-core" viewBox="0 0 32 32" width={size} height={size} fill="none">
        <title>Glitimė</title>
        {/* Everything that reads as *her face* — the lit disc and the eyes cut
            into it — turns together for the rare idle glance (`data-idle` in
            `mark.css`). The mask's own `.glit-eyes` group keeps its separate
            gaze-follow transform underneath this one, so a look sideways and
            a whole-head turn compose instead of fighting over one property. */}
        <g className="glit-face">
          {showEyes ? (
            <defs>
              <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
                <circle cx="16" cy="16" r="10.5" fill="white" />
                <g className="glit-eyes">
                  <rect
                    className="glit-eye"
                    x={16 - eyeGap - eyeW / 2}
                    y={15 - eyeH / 2}
                    width={eyeW}
                    height={eyeH}
                    rx={eyeH / 2}
                    fill="black"
                  />
                  <rect
                    className="glit-eye"
                    x={16 + eyeGap - eyeW / 2}
                    y={15 - eyeH / 2}
                    width={eyeW}
                    height={eyeH}
                    rx={eyeH / 2}
                    fill="black"
                  />
                </g>
              </mask>
            </defs>
          ) : null}
          <circle
            cx="16"
            cy="16"
            r="10.5"
            fill="var(--her, #8b74ff)"
            mask={showEyes ? `url(#${maskId})` : undefined}
          />
        </g>
      </svg>
    </span>
  )
}
